
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from './api';
import { ADMIN_DASHBOARD_ROLES } from '@cleanyarn/shared';

interface User {
  id: string;
  role: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  hasHydrated: boolean;

  login: (identifier: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  refreshSession: () => Promise<boolean>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      hasHydrated: false,

      login: async (identifier, password) => {
        const result = await api.login({ identifier, password });
        if (result.success && result.data) {
          const { accessToken, refreshToken, user } = result.data;

          // Check if user has admin panel access
          if (!ADMIN_DASHBOARD_ROLES.includes(user.role)) {
            return { success: false, error: 'Access denied. Staff accounts only.' };
          }

          api.setToken(accessToken);
          api.setRefreshHandler(() => get().refreshSession());
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
          });
          return { success: true };
        }
        return { success: false, error: result.error?.message ?? 'Login failed' };
      },

      logout: () => {
        api.logout().catch(() => {});
        api.setToken(null);
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        });
      },

      setTokens: (accessToken, refreshToken) => {
        api.setToken(accessToken);
        set({ accessToken, refreshToken, isAuthenticated: true });
      },

      refreshSession: async () => {
        const { refreshToken } = get();
        if (!refreshToken) return false;

        const result = await api.refreshToken(refreshToken);
        if (result.success && result.data) {
          api.setToken(result.data.accessToken);
          set({
            accessToken: result.data.accessToken,
            refreshToken: result.data.refreshToken,
          });
          return true;
        }

        get().logout();
        return false;
      },
    }),
    {
      name: 'cleanyarn-admin-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.hasHydrated = true;
          if (state.accessToken) {
            api.setToken(state.accessToken);
            api.setRefreshHandler(() => state.refreshSession());
          }
        }
      },
    }
  )
);
